import { useState, useEffect } from 'react';
import {
    Image,
    Video,
    Folder,
    FolderPlus,
    RefreshCw,
    ChevronRight,
    Upload,
    X,
    Loader2,
    CheckSquare,
    Square,
    Trash2,
    Download,
} from 'lucide-react';
import JSZip from 'jszip';
import { mediaApi } from '../lib/api';
import { formatBytes } from '../lib/utils';
import { useI18n } from '../i18n';
import { extractMetadata, type ImageMetadata } from '../services/metadata';
import ImageMetadataViewer from '../components/ImageMetadataViewer';
import type { MediaFile } from '../lib/types';

export default function Gallery() {
    const { t, formatRelativeTime } = useI18n();
    const [files, setFiles] = useState<MediaFile[]>([]);
    const [folders, setFolders] = useState<string[]>([]);
    const [currentFolder, setCurrentFolder] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
    const [showNewFolder, setShowNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
    const [selectedMetadata, setSelectedMetadata] = useState<ImageMetadata | null>(null);
    const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

    // Selection mode
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);
    const [isBulkMoving, setIsBulkMoving] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveTargetFolder, setMoveTargetFolder] = useState('');
    const [moveBrowserPath, setMoveBrowserPath] = useState('');
    const [moveBrowserFolders, setMoveBrowserFolders] = useState<string[]>([]);

    // Cache keys
    const CACHE_KEY = 'gallery_cache';
    const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

    // Get cached data for folder
    const getCachedData = (folder: string) => {
        try {
            const cache = localStorage.getItem(CACHE_KEY);
            if (!cache) return null;
            const data = JSON.parse(cache);
            const folderKey = folder || '_root';
            const cached = data[folderKey];
            if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
                return cached;
            }
        } catch {
            // Invalid cache
        }
        return null;
    };

    // Set cached data for folder
    const setCachedData = (folder: string, files: MediaFile[], folders: string[]) => {
        try {
            const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
            cache[folder || '_root'] = { files, folders, timestamp: Date.now() };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
        } catch {
            // Storage full or error
        }
    };

    // Clear all cache (on sync, upload, delete, move)
    const clearCache = () => {
        localStorage.removeItem(CACHE_KEY);
    };

    // Load files with cache
    const loadFiles = async (forceRefresh = false) => {
        const folder = currentFolder || '';

        // Try cache first (instant display)
        if (!forceRefresh) {
            const cached = getCachedData(folder);
            if (cached) {
                setFiles(cached.files);
                setFolders(cached.folders);
                setIsLoading(false);
                return;
            }
        }

        setIsLoading(true);
        try {
            const response = await mediaApi.list(folder || undefined);
            if (response.success) {
                setFiles(response.files);
                setFolders(response.folders);
                setCachedData(folder, response.files, response.folders);
            }
        } catch (error) {
            console.error('Failed to load files:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadFiles();
    }, [currentFolder]);

    // Handle sync
    const handleSync = async () => {
        setIsSyncing(true);
        try {
            const response = await mediaApi.sync();
            if (response.success) {
                alert(`${t.gallery.syncComplete}\n추가: ${response.added}개\n제거: ${response.removed}개`);
                clearCache();
                loadFiles(true);
            }
        } catch (error) {
            console.error('Sync failed:', error);
        } finally {
            setIsSyncing(false);
        }
    };

    // Handle delete
    const handleDelete = async (file: MediaFile) => {
        if (!confirm(`${t.gallery.deleteConfirm} (${file.filename})`)) return;

        try {
            await mediaApi.delete(file.id);
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
            setSelectedFile(null);
            setSelectedMetadata(null);
            clearCache(); // Invalidate cache after delete
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    // Handle file selection with metadata loading from R2
    const handleSelectFile = async (file: MediaFile) => {
        setSelectedFile(file);
        setSelectedMetadata(null);
        setIsLoadingMetadata(true);

        try {
            // Try to load .json metadata file from R2
            const metadataPath = file.r2_path.replace(/\.(png|webp|jpg|jpeg|gif)$/i, '.json');
            const metadataUrl = mediaApi.getFileUrl(metadataPath);
            const response = await fetch(metadataUrl, { credentials: 'include' });

            if (response.ok) {
                const metadata = await response.json();
                setSelectedMetadata(metadata);
            }
        } catch (error) {
            // Metadata file doesn't exist or failed to load - that's OK
            console.log('No metadata file found for:', file.filename);
        } finally {
            setIsLoadingMetadata(false);
        }
    };

    // Load folders for move browser (with cache)
    const loadMoveFolders = async (path: string) => {
        // Try cache first
        const cached = getCachedData(path);
        if (cached) {
            setMoveBrowserFolders(cached.folders);
            return;
        }

        try {
            const response = await mediaApi.list(path || undefined);
            if (response.success) {
                setMoveBrowserFolders(response.folders);
                setCachedData(path, response.files, response.folders);
            }
        } catch (error) {
            console.error('Failed to load folders:', error);
        }
    };

    // Open move modal with folder browser
    const openMoveModal = () => {
        setShowMoveModal(true);
        setMoveBrowserPath('');
        setMoveTargetFolder('');
        setMoveBrowserFolders([]); // Clear immediately
        loadMoveFolders('');
    };

    // Navigate to folder in move browser
    const navigateMoveBrowser = (folder: string) => {
        const newPath = folder.replace(/^media\/?/, '');
        setMoveBrowserPath(newPath);
        setMoveTargetFolder(newPath);
        setMoveBrowserFolders([]); // Clear immediately for instant feedback
        loadMoveFolders(newPath);
    };

    // Go up in move browser
    const goUpMoveBrowser = () => {
        const parts = moveBrowserPath.split('/').filter(Boolean);
        parts.pop();
        const newPath = parts.join('/');
        setMoveBrowserPath(newPath);
        setMoveTargetFolder(newPath);
        setMoveBrowserFolders([]); // Clear immediately
        loadMoveFolders(newPath);
    };

    // Selection mode helpers
    const toggleSelection = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const selectAll = () => {
        if (selectedIds.size === files.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(files.map((f) => f.id)));
        }
    };

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedIds(new Set());
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`${selectedIds.size}개 파일을 삭제하시겠습니까?`)) return;

        setIsBulkDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            for (const id of ids) {
                await mediaApi.delete(id);
            }
            setFiles((prev) => prev.filter((f) => !selectedIds.has(f.id)));
            setSelectedIds(new Set());
            clearCache(); // Invalidate cache
            alert(`${ids.length}개 파일이 삭제되었습니다.`);
        } catch (error) {
            console.error('Bulk delete failed:', error);
            alert('일부 파일 삭제에 실패했습니다.');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Bulk download as ZIP
    const handleBulkDownload = async () => {
        if (selectedIds.size === 0) return;

        setIsBulkDownloading(true);
        try {
            const zip = new JSZip();
            const selectedFiles = files.filter((f) => selectedIds.has(f.id));

            for (const file of selectedFiles) {
                const response = await fetch(mediaApi.getFileUrl(file.r2_path), {
                    credentials: 'include',
                });
                if (response.ok) {
                    const blob = await response.blob();
                    zip.file(file.filename, blob);
                }
            }

            const content = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.download = `gallery_${Date.now()}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Bulk download failed:', error);
            alert('다운로드에 실패했습니다.');
        } finally {
            setIsBulkDownloading(false);
        }
    };

    // Bulk move
    const handleBulkMove = async () => {
        if (selectedIds.size === 0) return;
        // Empty string means root folder (media/), which is valid

        setIsBulkMoving(true);
        setShowMoveModal(false);
        try {
            const ids = Array.from(selectedIds);
            let successCount = 0;
            for (const id of ids) {
                try {
                    await mediaApi.move(id, moveTargetFolder);
                    successCount++;
                } catch (err) {
                    console.error(`Move failed for id ${id}:`, err);
                }
            }
            if (successCount > 0) {
                alert(`${successCount}/${ids.length}개 파일이 이동되었습니다.`);
                clearCache();
                loadFiles(true);
            } else {
                alert('파일 이동에 실패했습니다.');
            }
            setSelectedIds(new Set());
            setMoveTargetFolder('');
        } catch (error) {
            console.error('Bulk move failed:', error);
            alert('이동에 실패했습니다.');
        } finally {
            setIsBulkMoving(false);
        }
    };

    // Handle upload
    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const uploadFiles = Array.from(e.target.files || []);
        if (uploadFiles.length === 0) return;

        setUploadProgress({ current: 0, total: uploadFiles.length });

        for (let i = 0; i < uploadFiles.length; i++) {
            const file = uploadFiles[i];
            try {
                // Extract metadata for image files
                let metadata = null;
                if (file.type.startsWith('image/')) {
                    metadata = await extractMetadata(file);
                }

                await mediaApi.upload(file, currentFolder || undefined, metadata || undefined);
                setUploadProgress({ current: i + 1, total: uploadFiles.length });
            } catch (error) {
                console.error('Upload failed:', error);
            }
        }

        setUploadProgress(null);
        clearCache();
        loadFiles(true); // Reload files after upload
        e.target.value = '';
    };

    // Handle create folder
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        try {
            const response = await mediaApi.createFolder(newFolderName, currentFolder || undefined);
            if (response.success && response.folder) {
                // Add new folder to state
                const newFolder = response.folder;
                setFolders((prev) => [...prev, newFolder].sort());
            }
            setNewFolderName('');
            setShowNewFolder(false);
        } catch (error) {
            console.error('Create folder failed:', error);
        }
    };

    // Navigate to folder
    const navigateToFolder = (folder: string) => {
        setCurrentFolder(folder);
    };

    // Get breadcrumb parts
    const breadcrumbs = currentFolder ? currentFolder.split('/') : [];

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files).filter(
            file => file.type.startsWith('image/') || file.type.startsWith('video/')
        );
        if (droppedFiles.length === 0) return;

        setUploadProgress({ current: 0, total: droppedFiles.length });

        for (let i = 0; i < droppedFiles.length; i++) {
            const file = droppedFiles[i];
            try {
                // Extract metadata for image files
                let metadata = null;
                if (file.type.startsWith('image/')) {
                    metadata = await extractMetadata(file);
                }
                await mediaApi.upload(file, currentFolder || undefined, metadata || undefined);
                setUploadProgress({ current: i + 1, total: droppedFiles.length });
            } catch (error) {
                console.error('Upload failed:', error);
            }
        }

        setUploadProgress(null);
        clearCache();
        loadFiles(true);
    };

    return (
        <div
            className="p-6 min-h-screen relative"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Drag overlay */}
            {isDragging && (
                <div className="absolute inset-0 z-50 bg-dark-900/90 flex items-center justify-center border-2 border-dashed border-accent-500 rounded-lg">
                    <div className="text-center">
                        <Upload className="w-12 h-12 text-accent-400 mx-auto mb-4" />
                        <p className="text-lg text-dark-200">{t.gallery.dragDrop}</p>
                    </div>
                </div>
            )}

            {/* Upload progress overlay */}
            {uploadProgress && (
                <div className="absolute inset-0 z-50 bg-dark-900/90 flex items-center justify-center">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 text-accent-400 mx-auto mb-4 animate-spin" />
                        <p className="text-lg text-dark-200">
                            업로드 중... {uploadProgress.current} / {uploadProgress.total}
                        </p>
                        <div className="w-48 bg-dark-700 rounded-full h-2 mt-4">
                            <div
                                className="bg-accent-500 h-2 rounded-full transition-all"
                                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Sync progress overlay */}
            {isSyncing && (
                <div className="absolute inset-0 z-50 bg-dark-900/90 flex items-center justify-center">
                    <div className="text-center">
                        <RefreshCw className="w-12 h-12 text-accent-400 mx-auto mb-4 animate-spin" />
                        <p className="text-lg text-dark-200">
                            {t.gallery.sync} 중...
                        </p>
                        <p className="text-sm text-dark-400 mt-2">
                            R2 스토리지와 동기화하는 중입니다
                        </p>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-dark-100">{t.gallery.title}</h1>
                    <p className="text-dark-400 mt-1">{t.gallery.description}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSync}
                        disabled={isSyncing}
                        className="btn-secondary"
                    >
                        <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                        {t.gallery.sync}
                    </button>
                    <button
                        onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                        className={`btn-secondary ${selectMode ? 'bg-accent-500 text-white' : ''}`}
                    >
                        {selectMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        {selectMode ? '취소' : '선택'}
                    </button>
                    <label className="btn-primary cursor-pointer">
                        <Upload className="w-4 h-4" />
                        {t.gallery.upload}
                        <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={handleUpload}
                            className="hidden"
                        />
                    </label>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-4 text-sm">
                <button
                    onClick={() => setCurrentFolder('')}
                    className="text-accent-400 hover:underline"
                >
                    Root
                </button>
                {breadcrumbs.map((part, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-dark-500" />
                        <button
                            onClick={() => navigateToFolder(breadcrumbs.slice(0, index + 1).join('/'))}
                            className="text-accent-400 hover:underline"
                        >
                            {part}
                        </button>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => setShowNewFolder(true)}
                    className="btn-ghost text-sm"
                >
                    <FolderPlus className="w-4 h-4" />
                    {t.gallery.newFolder}
                </button>
            </div>

            {/* Selection mode toolbar */}
            {selectMode && (
                <div className="flex items-center gap-3 p-3 bg-dark-800 rounded-lg mb-4">
                    <button onClick={selectAll} className="btn-secondary text-sm">
                        {selectedIds.size === files.length && files.length > 0 ? '전체 해제' : '전체 선택'}
                    </button>
                    <span className="text-dark-400 text-sm">
                        {selectedIds.size}개 선택됨
                    </span>

                    {selectedIds.size > 0 && (
                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={handleBulkDownload}
                                disabled={isBulkDownloading}
                                className="btn-secondary text-sm"
                            >
                                {isBulkDownloading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4" />
                                )}
                                다운로드
                            </button>
                            <button
                                onClick={openMoveModal}
                                disabled={isBulkMoving}
                                className="btn-secondary text-sm"
                            >
                                {isBulkMoving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Folder className="w-4 h-4" />
                                )}
                                이동
                            </button>
                            <button
                                onClick={handleBulkDelete}
                                disabled={isBulkDeleting}
                                className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm flex items-center gap-2"
                            >
                                {isBulkDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                삭제
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* New folder modal */}
            {showNewFolder && (
                <div className="modal-backdrop">
                    <div className="modal-content p-6">
                        <h3 className="text-lg font-semibold text-dark-100 mb-4">{t.gallery.newFolder}</h3>
                        <input
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="폴더 이름"
                            className="input mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowNewFolder(false)}
                                className="btn-secondary"
                            >
                                {t.common.cancel}
                            </button>
                            <button onClick={handleCreateFolder} className="btn-primary">
                                {t.common.add}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Move modal - Folder Explorer */}
            {showMoveModal && (
                <div className="modal-backdrop">
                    <div className="modal-content p-6 max-w-lg">
                        <h3 className="text-lg font-semibold text-dark-100 mb-4">
                            {selectedIds.size}개 파일 이동
                        </h3>

                        {/* Current path breadcrumb */}
                        <div className="flex items-center gap-2 mb-4 text-sm bg-dark-800 p-2 rounded-lg">
                            <button
                                onClick={() => navigateMoveBrowser('')}
                                className={`hover:text-accent-400 ${moveBrowserPath === '' ? 'text-accent-400' : 'text-dark-300'}`}
                            >
                                📁 Root
                            </button>
                            {moveBrowserPath.split('/').filter(Boolean).map((part, index, arr) => {
                                const path = arr.slice(0, index + 1).join('/');
                                return (
                                    <div key={path} className="flex items-center gap-2">
                                        <span className="text-dark-500">/</span>
                                        <button
                                            onClick={() => navigateMoveBrowser(`media/${path}`)}
                                            className={`hover:text-accent-400 ${moveBrowserPath === path ? 'text-accent-400' : 'text-dark-300'}`}
                                        >
                                            {part}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Folder list */}
                        <div className="bg-dark-800 rounded-lg p-2 mb-4 min-h-[150px] max-h-[250px] overflow-y-auto">
                            {moveBrowserPath && (
                                <button
                                    onClick={goUpMoveBrowser}
                                    className="w-full text-left px-3 py-2 text-dark-300 hover:bg-dark-700 rounded flex items-center gap-2"
                                >
                                    <span>⬆️</span> 상위 폴더
                                </button>
                            )}
                            {moveBrowserFolders.length > 0 ? (
                                moveBrowserFolders.map((folder) => (
                                    <button
                                        key={folder}
                                        onClick={() => navigateMoveBrowser(folder)}
                                        className="w-full text-left px-3 py-2 text-dark-200 hover:bg-dark-700 rounded flex items-center gap-2"
                                    >
                                        <Folder className="w-4 h-4 text-accent-400" />
                                        {folder.split('/').pop()}
                                    </button>
                                ))
                            ) : (
                                <p className="text-center text-dark-500 py-4">
                                    {moveBrowserPath ? '하위 폴더 없음' : '폴더 없음'}
                                </p>
                            )}
                        </div>

                        {/* Selected target path */}
                        <div className="mb-4">
                            <label className="text-sm text-dark-400 block mb-1">이동 대상 경로:</label>
                            <input
                                type="text"
                                value={moveTargetFolder}
                                onChange={(e) => setMoveTargetFolder(e.target.value)}
                                placeholder="/ (루트)"
                                className="input text-sm"
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowMoveModal(false);
                                    setMoveTargetFolder('');
                                }}
                                className="btn-secondary"
                            >
                                {t.common.cancel}
                            </button>
                            <button onClick={handleBulkMove} className="btn-primary">
                                여기로 이동
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="spinner text-accent-500 w-8 h-8" />
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {/* Folders - server returns only immediate subfolders */}
                    {folders.map((folder) => (
                        <button
                            key={folder}
                            onClick={() => navigateToFolder(folder)}
                            className="card-hover flex flex-col items-center gap-2 p-4"
                        >
                            <Folder className="w-12 h-12 text-accent-400" />
                            <span className="text-sm text-dark-200 truncate w-full text-center">
                                {folder.split('/').pop()}
                            </span>
                        </button>
                    ))}

                    {/* Files */}
                    {files.map((file) => (
                        <div
                            key={file.id}
                            onClick={() => selectMode ? toggleSelection(file.id) : handleSelectFile(file)}
                            className={`card-hover group relative aspect-square overflow-hidden cursor-pointer ${selectMode && selectedIds.has(file.id) ? 'ring-2 ring-accent-500' : ''
                                }`}
                        >
                            {file.file_type.startsWith('image/') ? (
                                <img
                                    src={mediaApi.getFileUrl(file.r2_path)}
                                    alt={file.filename}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            ) : file.file_type.startsWith('video/') ? (
                                <div className="w-full h-full bg-dark-800 flex items-center justify-center">
                                    <Video className="w-12 h-12 text-dark-500" />
                                </div>
                            ) : (
                                <div className="w-full h-full bg-dark-800 flex items-center justify-center">
                                    <Image className="w-12 h-12 text-dark-500" />
                                </div>
                            )}

                            {/* Selection checkbox */}
                            {selectMode && (
                                <div className="absolute top-2 left-2 z-10">
                                    {selectedIds.has(file.id) ? (
                                        <CheckSquare className="w-6 h-6 text-accent-400" />
                                    ) : (
                                        <Square className="w-6 h-6 text-dark-300" />
                                    )}
                                </div>
                            )}

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 left-0 right-0 p-2">
                                    <p className="text-xs text-dark-200 truncate">{file.filename}</p>
                                    <p className="text-xs text-dark-400">{formatBytes(file.size)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && files.length === 0 && folders.length === 0 && (
                <div className="text-center py-20">
                    <Image className="w-16 h-16 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-dark-300 mb-2">{t.gallery.noFiles}</h3>
                    <p className="text-dark-500">Upload media files or click "{t.gallery.sync}"</p>
                </div>
            )}

            {/* File detail modal */}
            {selectedFile && (
                <div className="modal-backdrop" onClick={() => setSelectedFile(null)}>
                    <div
                        className="modal-content max-w-4xl flex flex-col md:flex-row max-h-[90vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Image/Video preview */}
                        <div className="relative flex-1 min-h-0 bg-dark-950">
                            <button
                                onClick={() => setSelectedFile(null)}
                                className="absolute top-4 right-4 p-2 bg-dark-800 rounded-lg z-10 hover:bg-dark-700"
                            >
                                <X className="w-5 h-5 text-dark-300" />
                            </button>

                            {selectedFile.file_type.startsWith('image/') ? (
                                <img
                                    src={mediaApi.getFileUrl(selectedFile.r2_path)}
                                    alt={selectedFile.filename}
                                    className="w-full h-full object-contain max-h-[60vh] md:max-h-[80vh]"
                                />
                            ) : (
                                <video
                                    src={mediaApi.getFileUrl(selectedFile.r2_path)}
                                    controls
                                    className="w-full h-full object-contain max-h-[60vh] md:max-h-[80vh]"
                                />
                            )}
                        </div>

                        {/* Metadata sidebar */}
                        <div className="w-full md:w-80 p-4 border-t md:border-t-0 md:border-l border-dark-800 overflow-y-auto">
                            <h3 className="font-medium text-dark-100 mb-2">{selectedFile.filename}</h3>
                            <div className="text-sm text-dark-400 space-y-1 mb-4">
                                <p>Size: {formatBytes(selectedFile.size)}</p>
                                <p>{t.admin.users.createdAt}: {formatRelativeTime(selectedFile.created_at)}</p>
                            </div>

                            {isLoadingMetadata ? (
                                <div className="text-center py-4">
                                    <Loader2 className="w-6 h-6 text-accent-400 mx-auto animate-spin" />
                                    <p className="text-sm text-dark-400 mt-2">메타데이터 로드 중...</p>
                                </div>
                            ) : (
                                <ImageMetadataViewer
                                    metadata={selectedMetadata}
                                    filename={selectedFile.filename}
                                    path={selectedFile.r2_path}
                                    imageUrl={mediaApi.getFileUrl(selectedFile.r2_path)}
                                    onDelete={() => {
                                        handleDelete(selectedFile);
                                        setSelectedFile(null);
                                    }}
                                    onMove={() => {
                                        // Use the selected file for single move
                                        setSelectedIds(new Set([selectedFile.id]));
                                        setSelectedFile(null);
                                        openMoveModal();
                                    }}
                                    onAiTransfer={(mode) => {
                                        // TODO: Implement AI chat transfer
                                        alert(`AI 분석 기능 준비 중 (${mode})`);
                                    }}
                                    onApplySettings={() => {
                                        // TODO: Implement NAI generator settings apply
                                        alert('생성 설정 적용 기능 준비 중');
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
